import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/auth/AuthContext";
import { ThemeProvider } from "@/theme/ThemeProvider";
import { I18nProvider } from "@/i18n";
import { ConfirmProvider } from "@/components/ConfirmProvider";
import { AppShell } from "@/layout/AppShell";
import { api } from "@/api/client";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import DashboardPage from "@/pages/DashboardPage";
import {
  PartiesPage, PartyFormPage, InvoicesPage, InvoiceFormPage,
  BanksPage, BankAccountsPage, CashPage, CreditCardsPage, DBSPage,
  ChequesPage, ChequeDetailPage, ReceiptsPage, PaymentsPage,
  JournalsPage, AccountsPage, TrialBalancePage,
  SubscriptionsPage, RemindersPage, AttachmentsPage,
  AgingReportPage, VatReportPage,
} from "@/pages/modules";
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const OnboardingWizard = lazy(() => import("@/pages/OnboardingWizard"));
const OtherPaymentsPage = lazy(() => import("@/pages/OtherPaymentsPage"));
const OtherCollectionsPage = lazy(() => import("@/pages/OtherCollectionsPage"));
const UsersPage = lazy(() => import("@/pages/UsersPage"));
const PosPage = lazy(() => import("@/pages/PosPage"));
const CreditsPage = lazy(() => import("@/pages/CreditsPage"));

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Yükleniyor...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function OnboardingGate({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!user || user.role !== "admin") { setStatus({ completed: true }); return; }
    api.get("/settings/onboarding/status")
      .then((r) => setStatus(r.data))
      .catch(() => setStatus({ completed: true }));
  }, [user]);

  if (status === null) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Yükleniyor...</div>;
  }
  const isOnboardingRoute = loc.pathname.startsWith("/onboarding");
  if (!status.completed && user?.role === "admin" && !isOnboardingRoute) {
    return <Navigate to="/onboarding" replace />;
  }
  return children;
}

function App() {
  return (
    <ThemeProvider>
      <I18nProvider>
        <AuthProvider>
          <ConfirmProvider>
          <BrowserRouter>
            <Toaster position="top-right" richColors />
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/onboarding" element={
                <Protected>
                  <Suspense fallback={<div className="p-6">Yükleniyor...</div>}>
                    <OnboardingWizard />
                  </Suspense>
                </Protected>
              } />
              <Route element={<Protected><OnboardingGate><AppShell /></OnboardingGate></Protected>}>
                <Route index element={<DashboardPage />} />
                <Route path="parties" element={<PartiesPage />} />
                <Route path="parties/:id" element={<PartyFormPage />} />
                <Route path="invoices" element={<InvoicesPage />} />
                <Route path="invoices/:id" element={<InvoiceFormPage />} />
                <Route path="receipts" element={<ReceiptsPage />} />
                <Route path="payments" element={<PaymentsPage />} />
                <Route path="other-payments" element={<Suspense fallback={<div className="p-6">Yükleniyor...</div>}><OtherPaymentsPage /></Suspense>} />
                <Route path="other-collections" element={<Suspense fallback={<div className="p-6">Yükleniyor...</div>}><OtherCollectionsPage /></Suspense>} />
                <Route path="pos" element={<Suspense fallback={<div className="p-6">Yükleniyor...</div>}><PosPage /></Suspense>} />
                <Route path="credits" element={<Suspense fallback={<div className="p-6">Yükleniyor...</div>}><CreditsPage /></Suspense>} />
                <Route path="cheques" element={<ChequesPage />} />
                <Route path="cheques/incoming" element={<ChequesPage direction="incoming" />} />
                <Route path="cheques/outgoing" element={<ChequesPage direction="outgoing" />} />
                <Route path="cheques/endorsed" element={<ChequesPage direction="endorsed" />} />
                <Route path="cheques/own" element={<ChequesPage direction="own" />} />
                <Route path="bank-cheques" element={<ChequesPage direction="deposited" />} />
                <Route path="cheques/:id" element={<ChequeDetailPage />} />
                <Route path="banks" element={<BanksPage />} />
                <Route path="bank-accounts" element={<BankAccountsPage />} />
                <Route path="cash" element={<CashPage />} />
                <Route path="credit-cards" element={<CreditCardsPage />} />
                <Route path="dbs" element={<DBSPage />} />
                <Route path="accounts" element={<AccountsPage />} />
                <Route path="journals" element={<JournalsPage />} />
                <Route path="trial-balance" element={<TrialBalancePage />} />
                <Route path="subscriptions" element={<SubscriptionsPage />} />
                <Route path="reminders" element={<RemindersPage />} />
                <Route path="attachments" element={<AttachmentsPage />} />
                <Route path="reports/aging" element={<AgingReportPage />} />
                <Route path="reports/vat" element={<VatReportPage />} />
                <Route path="settings" element={<Suspense fallback={<div className="p-6">Yükleniyor...</div>}><SettingsPage /></Suspense>} />
                <Route path="users" element={<Suspense fallback={<div className="p-6">Yükleniyor...</div>}><UsersPage /></Suspense>} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          </ConfirmProvider>
        </AuthProvider>
      </I18nProvider>
    </ThemeProvider>
  );
}

export default App;
